import { AsyncPipe, DatePipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnInit
} from "@angular/core";
import {
    map,
    merge,
    Observable,
    ReplaySubject,
    scan,
    shareReplay,
    Subject,
    switchMap,
    tap
} from "rxjs";
import { RaskCommonModule } from "~app/common/rask-common.module";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { EudrService } from "~app/features/planera/services/eudr.service";
import { EudrAnmalanVM } from "./EudrAnmalanVM";

type EudrAnmalanState = {
    items: EudrAnmalanVM[];
    loading: boolean;
};

type EudrAnmalanReducer = (curr: EudrAnmalanState) => EudrAnmalanState;

@Component({
    selector: "rask-eudr-status",
    imports: [
        AsyncPipe,
        DatePipe,
        RaskCommonModule
    ],
    templateUrl: "./eudr-status.component.html",
    styleUrl: "./eudr-status.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EudrStatusComponent implements OnInit {
    protected eudrAnmalan$: Observable<EudrAnmalanState>;

    protected currentEudrState: EudrAnmalanState = {
        items: [],
        loading: false
    };

    private readonly eudrService = inject(EudrService);

    private readonly arbetsorderChangeSrc = new ReplaySubject<IArbetsorderDetail>(1);
    private readonly retrieveAnmalanSrc = new Subject<void>();

    private readonly arbetsorderGuid$ = this.arbetsorderChangeSrc.pipe(
        map(ao => ao.guid),
        shareReplay(1)
    );

    private readonly reload$ = this.arbetsorderGuid$.pipe(
        switchMap(aoId => this.eudrService.getAll(aoId)),
        map(res => res.map(r => new EudrAnmalanVM(r)))
    );

    private readonly retrieveAnmalan$ = this.retrieveAnmalanSrc.pipe(
        switchMap(() => this.arbetsorderGuid$)
    );

    private readonly fetchReducer: Observable<EudrAnmalanReducer> = this.reload$.pipe(
        map((res) => (curr) => ({
            ...curr,
            loading: false,
            items: res
        }))
    );

    private readonly loadingReducer:  Observable<EudrAnmalanReducer> = merge(
        this.retrieveAnmalanSrc,
        this.arbetsorderGuid$).pipe(
        map((_res) => (curr) => ({
            ...curr,
            loading: true
        }))
    );

    private readonly reloadReducer: Observable<EudrAnmalanReducer> = merge(
        this.retrieveAnmalan$).pipe(
        switchMap(() => this.fetchReducer)
    );

    private _arbetsorder: IArbetsorderDetail;

    get arbetsorder(): IArbetsorderDetail {
        return this._arbetsorder;
    }

    @Input() set arbetsorder(value: IArbetsorderDetail) {
        this._arbetsorder = value;
        this.arbetsorderChangeSrc.next(value);
    }

    ngOnInit(): void {
        this.eudrAnmalan$ = merge(
            this.reloadReducer,
            this.fetchReducer,
            this.loadingReducer
        ).pipe(
            scan((acc: EudrAnmalanState, mutatorFn: EudrAnmalanReducer) => mutatorFn(acc),
                {
                    items: [],
                    loading: false
                }),
            tap(state => {
                this.currentEudrState = state;
            })
        );
    }

    get savedReferensnummer(): string | null {
        return this.getActiveEudrRecord()?.referens || null;
    }

    get savedVerifieringsnummer(): string | null {
        return this.getActiveEudrRecord()?.verifiering || null;
    }

    get savedStatus(): string | null {
        return this.getActiveEudrRecord()?.status || null;
    }

    get savedSkickad(): Date | null {
        return this.getActiveEudrRecord()?.skickad || null;
    }

    get savedKvitterad(): Date | null {
        return this.getActiveEudrRecord()?.kvitterad || null;
    }

    protected getActiveEudrRecord(): EudrAnmalanVM | undefined {
        return this.currentEudrState.items.find(item => item.gallande === true);
    }

    protected onRetrieveClick() {
        this.retrieveAnmalanSrc.next();
    }
}
